import type {UseQueryResult} from 'react-query';
import {useQuery} from 'react-query';
import {z} from 'zod';
import {apiUrl} from '@/utils/api';
import {type Rental} from '@/types/Rental';
import {useUserContext} from '@/components/Providers/UserContext';

type UseFetchRentalsResponse = {
    data : {
        rentals: Rental[],
        user: {
            name: string;
        }
    },
    errorCode : number,
    message : string,
};

const rentalSchema = z.object({
    checkInTimeStamp : z.string(),
    checkoutTimestamp : z.string(),
    dueDate : z.string(),
    inOutStatus : z.string(),
    itemName : z.string(),
    itemNum : z.string(),
    itemStatus : z.string(),
    serialNum : z.string(),
    type : z.string(),
});

const useFetchRentalsResponseSchema = z.object({
    data : z.object({
        rentals: z.array(rentalSchema),
        user: z.object({
            name: z.string(),
        })
    }),
    errorCode : z.number(),
    message : z.string(),
});

const useFetchRentals = (rentalId ?: string) : UseQueryResult<UseFetchRentalsResponse> => {
    const {user, setUser} = useUserContext();
    const url = '/v1/rental' + (rentalId && rentalId !== '' ? `/${rentalId}` : '');

    return useQuery(
        ['rental'],
        async ({signal}) => {
            const response = await fetch(
                apiUrl(url).toString(),
                {
                    signal: (signal ?? null),
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${user?.jwt}`,
                    },
                }
            );

            if (response.status === 401) {
                setUser(null);
            }

            if (!response.ok) {
                throw new Error('Unable to fetch rental.');
            }

            const json = await response.json() as UseFetchRentalsResponse;

            try {
                useFetchRentalsResponseSchema.parse(json);
            } catch (error: unknown) {
                console.log('error', error);
                throw new Error('Unable to parse rental response.');
            }

            return json;
        },
        {
            enabled: user !== undefined
        }
    );
};

export default useFetchRentals;
